// ═══════════════════════════════════════════════════════════════════════════════
//  src/dojo/dojoApi.js
//  EL DOJO — la única capa que habla con el servidor
//
//  Tres de los cuatro formatos son 100% locales: contenido estático, voz del
//  navegador y juicio determinista. No cuestan un token ni un milisegundo de
//  servidor, y funcionan sin conexión.
//
//  Sólo Crisis Room necesita IA — porque evaluar si alguien "logró calmar al
//  cliente" no se puede resolver con una tabla de respuestas correctas.
// ═══════════════════════════════════════════════════════════════════════════════

const BASE = `${import.meta.env.VITE_API_URL || ''}`;

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Evalúa una respuesta de Crisis Room. El jefe juzga la misión antes que la
 * gramática — eso lo decide el prompt del backend, no esta capa.
 *
 * @returns {Promise<{mission:string, verdict_es:string, cost:string[],
 *                    authority:number, evaluated:boolean}>}
 */
export async function evaluateCrisis({ scenarioId, transcript, seconds }) {
  const res = await fetch(`${BASE}/api/v3/dojo/crisis/evaluate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      scenario_id: scenarioId,
      transcript,
      duration_seconds: seconds,
    }),
  });

  if (!res.ok) {
    let detail = 'No pudimos evaluar tu respuesta.';
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string') detail = body.detail;
      else if (body?.detail?.message) detail = body.detail.message;
    } catch { /* respuesta sin JSON */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }

  return res.json();
}

/**
 * Suma el esfuerzo de la sesión al progreso del alumno.
 *
 * Reutiliza el endpoint de minijuegos a propósito: ya suma XP y ya alimenta
 * `DailyActivityLog`, que es de donde salen las rachas. Un endpoint nuevo sólo
 * habría creado un segundo lugar donde se calcula lo mismo.
 *
 * Es best-effort: si falla, el alumno no se entera. Perder XP de una sesión es
 * molesto; romperle la pantalla de resultado por eso, peor.
 */
export async function saveDojoSession({ format, score, correct, total }) {
  try {
    await fetch(`${BASE}/api/minigames/save-score`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        game_name: `dojo_${format}`,
        score,
        correct_answers: correct,
        total_questions: total,
      }),
    });
  } catch { /* silencioso a propósito */ }
}
